import React from 'react';
import { motion } from 'framer-motion';
import {
  ProductCardSkeleton,
  TableSkeleton,
  GridSkeleton,
  FormSkeleton,
  CardSkeleton,
  ListSkeleton,
  PageSkeleton,
  ModalSkeleton,
  StatsSkeleton,
  ChartSkeleton,
} from './SkeletonComponents';

export interface LoadingStateProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  text?: string;
  fullScreen?: boolean;
  variant?: 'default' | 'admin' | 'user';
  className?: string;
  skeleton?: boolean;
  skeletonType?:
    | 'product-card'
    | 'table'
    | 'grid'
    | 'form'
    | 'card'
    | 'list'
    | 'page'
    | 'modal'
    | 'stats'
    | 'chart';
  skeletonProps?: any;
}

const skeletonMap = {
  'product-card': ProductCardSkeleton,
  table: TableSkeleton,
  grid: GridSkeleton,
  form: FormSkeleton,
  card: CardSkeleton,
  list: ListSkeleton,
  page: PageSkeleton,
  modal: ModalSkeleton,
  stats: StatsSkeleton,
  chart: ChartSkeleton,
};

export const LoadingState: React.FC<LoadingStateProps> = ({
  size = 'md',
  text,
  fullScreen = false,
  variant = 'default',
  className = '',
  skeleton = false,
  skeletonType = 'card',
  skeletonProps = {},
}) => {
  if (skeleton && skeletonMap[skeletonType]) {
    const SkeletonComponent = skeletonMap[skeletonType];
    return <SkeletonComponent {...skeletonProps} />;
  }

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'w-4 h-4';
      case 'lg':
        return 'w-8 h-8';
      case 'xl':
        return 'w-12 h-12';
      default:
        return 'w-6 h-6';
    }
  };

  const getVariantClasses = () => {
    switch (variant) {
      case 'admin':
        return 'border-blue-200 border-t-blue-600';
      case 'user':
        return 'border-green-200 border-t-green-600';
      default:
        return 'border-gray-200 border-t-gray-600';
    }
  };

  const getTextSizeClass = () => {
    switch (size) {
      case 'xl':
        return 'text-lg';
      case 'lg':
        return 'text-base';
      case 'sm':
        return 'text-xs';
      default:
        return 'text-sm';
    }
  };

  const spinner = (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={`flex flex-col items-center justify-center gap-3 ${className}`}
    >
      <div
        className={`${getSizeClasses()} ${getVariantClasses()} border-2 border-solid rounded-full animate-spin`}
      />
      {text && <p className={`${getTextSizeClass()} font-medium text-gray-600`}>{text}</p>}
    </motion.div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6 shadow-xl">{spinner}</div>
      </div>
    );
  }

  return spinner;
};

export const AdminLoadingState: React.FC<{ text?: string }> = ({ text = 'Yükleniyor...' }) => (
  <div className="flex items-center justify-center h-64">
    <LoadingState size="lg" text={text} variant="admin" />
  </div>
);

export const InlineLoading: React.FC<{ text?: string; variant?: 'light' | 'dark' }> = ({
  text = 'Yükleniyor...',
  variant = 'light',
}) => (
  <div className="flex items-center justify-center gap-2">
    <div
      className={`w-4 h-4 border-2 rounded-full animate-spin ${variant === 'light' ? 'border-white/30 border-t-white' : 'border-gray-300 border-t-gray-600'}`}
    />
    <span>{text}</span>
  </div>
);
